import { useEffect } from "react";
import { useLocation } from "react-router";
import type { ViewSource } from "../../shared/marketStats";

/**
 * Where a visit to a listing came from, for the seller's stats (listing_view_sources): the page the
 * buyer was on before, or how they reached the site — a shop email, its QR / short link, another site —
 * which counts for the next half hour of that visit.
 */
const ENTRY_KEY = "pf-entry-src";
const ENTRY_MS = 30 * 60_000;
let previous: string | null = null;
let current: string | null = null;

function rememberEntry(search: string, referrer: string): void {
  const ref = new URLSearchParams(search).get("ref");
  let source: ViewSource | null = ref === "email" ? "email" : ref === "qr" ? "qr" : ref ? "external" : null;
  if (!source && referrer) {
    try {
      if (new URL(referrer).host !== window.location.host) source = "external";
    } catch {
      // Not a URL: nothing to learn from it.
    }
  }
  if (!source) return;
  try {
    sessionStorage.setItem(ENTRY_KEY, JSON.stringify({ source, at: Date.now() }));
  } catch {
    // No storage: the page they're on still knows its ?ref.
  }
}

function entrySource(): ViewSource | null {
  try {
    const raw = JSON.parse(sessionStorage.getItem(ENTRY_KEY) ?? "null") as { source: ViewSource; at: number } | null;
    return raw && Date.now() - raw.at < ENTRY_MS ? raw.source : null;
  } catch {
    return null;
  }
}

/** Mounted once in the layout: notes each page so a listing knows the one before it. */
export function VisitTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    rememberEntry(window.location.search, document.referrer);
  }, []);
  useEffect(() => {
    const here = pathname + search;
    if (here !== current) {
      previous = current;
      current = here;
    }
  }, [pathname, search]);
  return null;
}

/** The source of a listing visit: a link that says so (state), its own ?ref, how the visit started, or the page before. */
export function viewSource(state: unknown, search: string): ViewSource {
  const said = (state as { src?: unknown } | null)?.src;
  if (said === "featured") return "featured";
  const ref = new URLSearchParams(search).get("ref");
  if (ref === "email" || ref === "qr") return ref;
  const entry = entrySource();
  if (entry) return entry;
  // The listing renders before the tracker notes it: "current" is still the page the buyer came from.
  const here = typeof window === "undefined" ? "" : window.location.pathname + window.location.search;
  const before = current !== null && current !== here ? current : previous;
  if (before === null) return "external";
  if (before.startsWith("/vendedores/") || before.startsWith("/@")) return "shop";
  if (before.startsWith("/producto/")) return "product";
  if (/^\/mercado\/\d/.test(before)) return "other";
  if (before.startsWith("/mercado") || before.startsWith("/buscar")) return "search";
  return "other";
}
